import type { PlanFocus } from '@features/workshop-plan/utils/plan-cell';

import { useState } from 'react';
import { LayoutGrid } from 'lucide-react';

import { Skeleton } from '@shared/components/ui/skeleton';
import { EmptyContent } from '@shared/components/empty-content';
import { WarehouseBreadcrumbs } from '@shared/components/breadcrumbs';

import { PlanZone } from '@features/workshop-plan/components/plan-zone';
import { PlanToolbar } from '@features/workshop-plan/components/plan-toolbar';
import { FamilyDemandBar } from '@features/workshop-plan/components/family-demand-bar';
import { useGetWorkshopPlansQuery } from '@features/workshop-plan/api/workshop-plan-api';
import { NO_FOCUS, countTones, getPlanCells, getCellStatus } from '@features/workshop-plan/utils/plan-cell';

const PAGE_TITLE = "Plan de l'atelier";
const REFRESH_INTERVAL_MS = 120_000;

export const WorkshopPlanView = () => {
  const {
    data: plans,
    isLoading,
    isError,
  } = useGetWorkshopPlansQuery(undefined, {
    pollingInterval: REFRESH_INTERVAL_MS,
    skipPollingIfUnfocused: true,
  });

  const [focus, setFocus] = useState<PlanFocus>(NO_FOCUS);

  const cells = getPlanCells(plans ?? []);
  const occupied = cells.filter((cell) => getCellStatus(cell).occupied).length;
  const tones = countTones(cells);

  return (
    <div className="mx-auto flex w-full max-w-450 flex-col gap-6">
      <WarehouseBreadcrumbs breadcrumbs={[{ name: 'Atelier' }, { name: PAGE_TITLE }]} page={PAGE_TITLE} />

      <FamilyDemandBar />

      {isLoading && <Skeleton className="h-80 rounded-lg" />}

      {isError && (
        <EmptyContent
          description="Le plan n'a pas pu être chargé. Il se rafraîchit automatiquement."
          icon={<LayoutGrid />}
          title="Chargement impossible"
        />
      )}

      {plans && plans.length > 0 && (
        <PlanToolbar
          counts={{ ...tones, empty: cells.length - occupied }}
          focus={focus}
          occupied={occupied}
          onFocusChange={setFocus}
          total={cells.length}
        />
      )}

      {plans?.length === 0 && (
        <EmptyContent
          description="Aucune zone de type atelier n'est encore placée sur le plan."
          icon={<LayoutGrid />}
          title="Aucun plan de l'atelier"
        />
      )}

      {plans?.map((plan) => (
        <PlanZone focus={focus} key={plan.id} plan={plan} />
      ))}
    </div>
  );
};
